/**
 * Mount Rigi Hero
 * Video hover, mobile autoplay and sound controls.
 */

(() => {
  const HERO_SELECTOR = '[data-mr-hero]';
  const INITIALIZED_ATTRIBUTE = 'data-mr-hero-initialized';

  function initializeHero(hero) {
    if (!hero || hero.getAttribute(INITIALIZED_ATTRIBUTE) === 'true') {
      return;
    }

    hero.setAttribute(INITIALIZED_ATTRIBUTE, 'true');

    const cards = [...hero.querySelectorAll('[data-mr-hero-card]')];

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

    const supportsHover = window.matchMedia(
      '(hover: hover) and (pointer: fine)'
    );

    const isMobile = window.matchMedia('(max-width: 749px)');

    const getVideo = (card) => card.querySelector('video');

    const pauseVideo = (video, reset = false) => {
      if (!video) return;

      video.pause();

      if (reset) {
        try {
          video.currentTime = 0;
        } catch (error) {
          /*
           * Manche Browser erlauben currentTime erst,
           * nachdem die Metadaten geladen wurden.
           */
        }
      }
    };

    const playVideo = async (video) => {
      if (!video) return;

      try {
        await video.play();
      } catch (error) {
        /*
         * Autoplay kann durch Browser-, Geräte- oder
         * Energiespareinstellungen blockiert werden.
         */
      }
    };

    const updateSoundButton = (button, soundEnabled) => {
      if (!button) return;

      button.setAttribute('aria-pressed', String(soundEnabled));

      button.setAttribute(
        'aria-label',
        soundEnabled ? 'Turn sound off' : 'Turn sound on'
      );
    };

    const muteOtherVideos = (activeVideo) => {
      cards.forEach((card) => {
        const video = getVideo(card);
        const button = card.querySelector('[data-mr-sound-toggle]');

        if (!video || video === activeVideo) return;

        video.muted = true;
        video.defaultMuted = true;

        updateSoundButton(button, false);
      });
    };

    cards.forEach((card) => {
      const video = getVideo(card);
      const soundButton = card.querySelector('[data-mr-sound-toggle]');

      if (!video) return;

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');

      card.addEventListener('mouseenter', () => {
        if (!supportsHover.matches || prefersReducedMotion.matches) return;

        playVideo(video);
      });

      card.addEventListener('mouseleave', () => {
        if (!supportsHover.matches) return;

        pauseVideo(video, true);

        /*
         * Ein angeklickter Sound-Button behält sonst den Fokus.
         * Dadurch würde :focus-within die Karte vorne halten.
         */
        const focusedElement = document.activeElement;

        if (
          focusedElement instanceof HTMLElement &&
          card.contains(focusedElement)
        ) {
          focusedElement.blur();
        }
      });

      card.addEventListener('focusin', () => {
        if (prefersReducedMotion.matches) return;

        playVideo(video);
      });

      card.addEventListener('focusout', (event) => {
        if (card.contains(event.relatedTarget)) return;

        pauseVideo(video, true);
      });

      if (soundButton) {
        soundButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          const shouldEnableSound = video.muted;

          muteOtherVideos(video);

          video.muted = !shouldEnableSound;
          video.defaultMuted = !shouldEnableSound;

          updateSoundButton(soundButton, shouldEnableSound);

          playVideo(video);
        });
      }
    });

    const featuredVideo = hero.querySelector(
      '.mr-hero-card.is-featured video'
    );

    const startMobileVideo = () => {
      if (!featuredVideo) return;

      /*
       * Diese Eigenschaften müssen vor video.play()
       * gesetzt werden, damit Mobile-Browser Autoplay erlauben.
       */
      featuredVideo.muted = true;
      featuredVideo.defaultMuted = true;
      featuredVideo.playsInline = true;
      featuredVideo.autoplay = true;
      featuredVideo.loop = true;

      featuredVideo.setAttribute('muted', '');
      featuredVideo.setAttribute('playsinline', '');
      featuredVideo.setAttribute('autoplay', '');
      featuredVideo.setAttribute('loop', '');

      const soundButton = featuredVideo
        .closest('[data-mr-hero-card]')
        ?.querySelector('[data-mr-sound-toggle]');

      updateSoundButton(soundButton, false);

      playVideo(featuredVideo);
    };

    const updateMobilePlayback = () => {
      if (!featuredVideo) return;

      if (isMobile.matches && !prefersReducedMotion.matches) {
        startMobileVideo();
        return;
      }

      /*
       * Desktop-Videos werden nur über Hover oder Fokus gestartet.
       */
      if (!isMobile.matches && !supportsHover.matches) {
        pauseVideo(featuredVideo);
      }
    };

    updateMobilePlayback();

    /*
     * Nochmals starten, sobald das Video ausreichend geladen ist.
     * Das erhöht die Zuverlässigkeit auf Safari und iOS.
     */
    if (featuredVideo) {
      featuredVideo.addEventListener(
        'loadeddata',
        () => {
          if (isMobile.matches && !prefersReducedMotion.matches) {
            startMobileVideo();
          }
        },
        { once: true }
      );

      featuredVideo.addEventListener(
        'canplay',
        () => {
          if (
            isMobile.matches &&
            !prefersReducedMotion.matches &&
            featuredVideo.paused
          ) {
            startMobileVideo();
          }
        },
        { once: true }
      );
    }

    isMobile.addEventListener?.('change', updateMobilePlayback);

    prefersReducedMotion.addEventListener?.(
      'change',
      updateMobilePlayback
    );

    /*
     * Stoppt Videos ausserhalb des sichtbaren Bereichs,
     * um Akku, CPU und Datenverbrauch zu reduzieren.
     */
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (
                isMobile.matches &&
                featuredVideo &&
                !prefersReducedMotion.matches
              ) {
                startMobileVideo();
              }

              return;
            }

            cards.forEach((card) => {
              pauseVideo(getVideo(card));
            });
          });
        },
        {
          threshold: 0.15
        }
      );

      observer.observe(hero);
    }
  }

  function initializeAllHeroes(root = document) {
    root.querySelectorAll(HERO_SELECTOR).forEach(initializeHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeAllHeroes();
    });
  } else {
    initializeAllHeroes();
  }

  /*
   * Shopify Theme Editor:
   * Neu geladene Sections erneut initialisieren.
   */
  document.addEventListener('shopify:section:load', (event) => {
    initializeAllHeroes(event.target);
  });
})();